import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CatalogProductService } from 'src/app/services/catalog-product.service';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-catalog-product-detail',
  templateUrl: './catalog-product-detail.component.html',
  styleUrls: ['./catalog-product-detail.component.css']
})
export class CatalogProductDetailComponent implements OnInit, OnDestroy {

  loading = false;
  loadingInvoices = false;
  loadingStatsDetail = false;
  saving = false;

  product: any = null;
  invoices: any[] = [];
  invoicePagination: any = { page: 1, limit: 20, total: 0, pages: 0 };

  // Stats detail
  statsDetail: any = null;

  // Active tab
  activeTab: string = 'overview';

  // Edit mode
  editing = false;
  editForm: any = { name: '', brand: '', category: '', material: '', numero_material: '', se: null };

  // Charts
  private shopChart: Chart | null = null;

  sectorNames: { [key: number]: string } = {
    10: 'Común',
    11: 'Calzados',
    12: 'Marroquinería',
    13: 'Bazar',
    14: 'Vestimenta',
    15: 'Juguetes',
    16: 'Accesorios',
    17: 'Belleza',
    18: 'Ferretería',
    19: 'Electrodomésticos',
    20: 'Celulares',
    21: 'Hogar',
    22: 'Deportes',
    23: 'Bebes',
    24: 'Tecnología',
    25: 'Artículos de oficina',
    26: 'Suministros',
    27: 'Alimentos',
    28: 'Farmacia',
    29: 'Licorería y conexos',
    30: 'Cuidado personal y Hogar'
  };

  getSectorName(se: number): string {
    return this.sectorNames[se] || `Sector ${se}`;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private catalogService: CatalogProductService
  ) {}

  ngOnInit(): void {
    const code = this.route.snapshot.paramMap.get('code');
    if (code) {
      this.loadAll(code);
    }
  }

  ngOnDestroy(): void {
    this.shopChart?.destroy();
  }

  async loadAll(code: string) {
    await Promise.all([
      this.loadProduct(code),
      this.loadInvoices(code),
      this.loadStatsDetail(code)
    ]);
  }

  async loadProduct(code: string) {
    this.loading = true;
    try {
      const resp = await this.catalogService.getByCode(code);
      this.product = resp.res;
      this.editForm = {
        name: this.product.name || '',
        brand: this.product.brand || '',
        category: this.product.category || '',
        material: this.product.material || '',
        numero_material: this.product.numero_material || '',
        se: this.product.se || null
      };
    } catch (error) {
      console.error('Error cargando producto:', error);
    } finally {
      this.loading = false;
    }
  }

  async loadInvoices(code: string) {
    this.loadingInvoices = true;
    try {
      const resp = await this.catalogService.getInvoicesByProduct(code, {
        page: this.invoicePagination.page,
        limit: this.invoicePagination.limit
      });
      this.invoices = resp.res || [];
      this.invoicePagination = resp.pagination || this.invoicePagination;
    } catch (error) {
      console.error('Error cargando créditos:', error);
    } finally {
      this.loadingInvoices = false;
    }
  }

  async loadStatsDetail(code: string) {
    this.loadingStatsDetail = true;
    try {
      const resp = await this.catalogService.getProductStatsDetail(code);
      this.statsDetail = resp.res;
      setTimeout(() => this.buildShopChart(), 100);
    } catch (error) {
      console.error('Error cargando detalle de stats:', error);
    } finally {
      this.loadingStatsDetail = false;
    }
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    if (tab === 'overview') {
      setTimeout(() => {
        this.buildShopChart();
      }, 100);
    }
  }

  // === CHARTS ===

  buildShopChart() {
    this.shopChart?.destroy();
    this.shopChart = null;
    const canvas = document.getElementById('shopBreakdownChart') as HTMLCanvasElement;
    if (!canvas || !this.statsDetail?.by_shop?.length) return;

    const data = this.statsDetail.by_shop;
    this.shopChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: data.map((d: any) => `Tienda ${d.shop}`),
        datasets: [{
          label: 'Unidades vendidas',
          data: data.map((d: any) => d.total_units),
          backgroundColor: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#6366f1'],
          borderRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true } }
      }
    });
  }

  // === TABLE ===

  goToInvoicePage(page: number) {
    if (page < 1 || page > this.invoicePagination.pages) return;
    this.invoicePagination.page = page;
    this.loadInvoices(this.product.code);
  }

  toggleEdit() {
    this.editing = !this.editing;
    if (this.editing) {
      this.editForm = {
        name: this.product.name || '',
        brand: this.product.brand || '',
        category: this.product.category || '',
        material: this.product.material || '',
        numero_material: this.product.numero_material || '',
        se: this.product.se || null
      };
    }
  }

  async saveEdit() {
    this.saving = true;
    try {
      const resp = await this.catalogService.updateProduct(this.product.code, this.editForm);
      this.product = resp.res;
      this.editing = false;
    } catch (error) {
      console.error('Error actualizando producto:', error);
    } finally {
      this.saving = false;
    }
  }

  goBack() {
    this.router.navigate(['/console/catalog-products']);
  }

  formatCurrency(value: number): string {
    if (!value && value !== 0) return '-';
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatNumber(value: number): string {
    if (!value && value !== 0) return '0';
    return value.toLocaleString('es-VE');
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'PaymentComplete': return 'bg-green-100 text-green-800';
      case 'PaymentPending': return 'bg-yellow-100 text-yellow-800';
      case 'PaymentTrash': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusName(status: string): string {
    switch (status) {
      case 'PaymentComplete': return 'Completado';
      case 'PaymentPending': return 'Pendiente';
      case 'PaymentTrash': return 'Borrador';
      default: return status || '-';
    }
  }
}
